import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type TextareaHTMLAttributes,
} from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { cx } from '@/lib/cx'

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
  /** Grows with the content, so the doctor sees what they typed without
   *  scrolling inside a small box mid-sentence. */
  autoGrow?: boolean
  showCounter?: boolean
  maxLength?: number
}

export const TextArea = forwardRef<HTMLTextAreaElement, Props>(function TextArea(
  { label, required, error, hint, autoGrow = true, showCounter = false, maxLength, value, className, ...rest },
  forwardedRef,
) {
  const ref = useRef<HTMLTextAreaElement>(null)
  // The caller needs the node to move focus into Notes on open; the component
  // needs it to auto-grow. Both share one ref.
  useImperativeHandle(forwardedRef, () => ref.current as HTMLTextAreaElement, [])

  useEffect(() => {
    if (!autoGrow || !ref.current) return
    const el = ref.current
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value, autoGrow])

  const length = typeof value === 'string' ? value.length : 0

  return (
    <Field label={label} required={required ?? false} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <>
          <textarea
            id={id}
            ref={ref}
            value={value}
            maxLength={maxLength}
            className={cx(
              FIELD_CONTROL,
              // Clinical notes are prose: a 120-character measure on a wide
              // monitor is unreadable.
              'min-h-notes max-w-prose resize-y leading-relaxed',
              invalid && FIELD_INVALID,
              className,
            )}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            autoComplete="off"
            {...rest}
          />
          {showCounter && maxLength && (
            <span className="self-end text-xs text-fg-subtle tabular-nums">
              {length}/{maxLength}
            </span>
          )}
        </>
      )}
    </Field>
  )
})
